import { useState, useCallback } from "react";
import { stravaApi, StravaAnalysis, AnalysisErrorCode } from "@/api/strava";
import { triggerGlobalToast } from "@/contexts/ToastContext";
import { AxiosError } from "axios";

interface AnalysisState {
  analysis: StravaAnalysis | null;
  isLoading: boolean;
  isAnalyzing: boolean;
  error: { code: AnalysisErrorCode; message: string } | null;
  rateLimitMinutes: number | null;
  noActivities: boolean; // User has no run data (valid state, not error)
}

export function useStravaAnalysis() {
  const [state, setState] = useState<AnalysisState>({
    analysis: null,
    isLoading: false,
    isAnalyzing: false,
    error: null,
    rateLimitMinutes: null,
    noActivities: false,
  });

  /**
   * Fetch existing analysis (if any)
   */
  const fetchAnalysis = useCallback(async () => {
    try {
      setState((prev) => ({ ...prev, isLoading: true, error: null }));
      const { analysis } = await stravaApi.getAnalysis();
      setState((prev) => ({ ...prev, analysis, isLoading: false }));
      return analysis;
    } catch (error) {
      console.error("Failed to fetch analysis:", error);
      setState((prev) => ({ ...prev, isLoading: false }));
      return null;
    }
  }, []);

  /**
   * Trigger new analysis generation
   */
  const runAnalysis = useCallback(async () => {
    try {
      setState((prev) => ({
        ...prev,
        isAnalyzing: true,
        error: null,
        rateLimitMinutes: null,
        noActivities: false,
      }));

      const response = await stravaApi.triggerAnalysis();

      // Handle noActivities - this is a success case, not an error
      // User has Strava connected but no running data yet
      if (response.noActivities) {
        setState((prev) => ({
          ...prev,
          analysis: null,
          isAnalyzing: false,
          noActivities: true,
        }));
        // No toast - UI will show the NewRunnerScreen
        return null;
      }

      setState((prev) => ({
        ...prev,
        analysis: response.analysis,
        isAnalyzing: false,
      }));

      triggerGlobalToast("Analysis complete!", "success");
      return response.analysis;
    } catch (error) {
      console.error("Failed to run analysis:", error);

      const axiosError = error as AxiosError<{
        error: string;
        message?: string;
        minutesUntilAllowed?: number;
      }>;

      // Handle rate limit (429)
      if (axiosError.response?.status === 429) {
        const minutes = axiosError.response.data?.minutesUntilAllowed || 60;
        setState((prev) => ({
          ...prev,
          isAnalyzing: false,
          rateLimitMinutes: minutes,
          error: {
            code: "RATE_LIMITED",
            message: `Please wait ${minutes} minutes before analyzing again`,
          },
        }));
        triggerGlobalToast(
          `Please wait ${minutes} minutes before analyzing again`,
          "warning"
        );
        return null;
      }

      // Handle other errors - backend sends { error: "CODE", message: "..." }
      const errorCode = axiosError.response?.data?.error as AnalysisErrorCode | undefined;
      const errorMessage = axiosError.response?.data?.message;

      if (errorCode) {
        setState((prev) => ({
          ...prev,
          isAnalyzing: false,
          error: { code: errorCode, message: errorMessage || "Something went wrong" },
        }));

        // Show user-friendly messages
        switch (errorCode) {
          case "NO_STRAVA_CONNECTION":
            triggerGlobalToast("Please connect your Strava account first", "error");
            break;
          case "TOKEN_EXPIRED":
            triggerGlobalToast("Your Strava connection expired. Please reconnect.", "error");
            break;
          default:
            triggerGlobalToast(errorMessage || "Analysis failed", "error");
        }
        return null;
      }

      // Generic error
      setState((prev) => ({
        ...prev,
        isAnalyzing: false,
        error: { code: "ANALYSIS_FAILED", message: "Something went wrong" },
      }));
      triggerGlobalToast("Analysis failed. Please try again.", "error");
      return null;
    }
  }, []);

  /**
   * Clear error state
   */
  const clearError = useCallback(() => {
    setState((prev) => ({ ...prev, error: null, rateLimitMinutes: null }));
  }, []);

  return {
    analysis: state.analysis,
    isLoading: state.isLoading,
    isAnalyzing: state.isAnalyzing,
    error: state.error,
    rateLimitMinutes: state.rateLimitMinutes,
    noActivities: state.noActivities,
    hasAnalysis: state.analysis !== null,
    fetchAnalysis,
    runAnalysis,
    clearError,
  };
}
